// Pedigree.jsx (no MUI)
import React, { useEffect, useState } from "react";
import moment from "moment";
import AIReactionModal from "../../../../RayCustomControls/AIContent/AIReactionModal";
import BaseServiceApi from "BaseServiceApi";
import UserInfoUtil from "UserInfoUtil";
const EntitlementType = BaseServiceApi.rayData["EntitlementType"];
// --- lightweight layout helpers (no external deps) ---
const Row = ({ gap = 16, style = {}, children, ...rest }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(12, 1fr)",
      gap,
      ...style,
    }}
    {...rest}
  >
    {children}
  </div>
);

const Col = ({ span = 12, style = {}, children, ...rest }) => (
  <div style={{ gridColumn: `span ${span}`, ...style }} {...rest}>
    {children}
  </div>
);

// --- label maps (unchanged) ---
const strategic_vision_and_execution = {
  LongTerm_Strategy: "LongTerm Strategy",
  Mergers_and_Acquisitions: "Mergers and Acquisitions",
  Innovation_and_R_and_D: "Innovation and R&D",
  Adaptability: "Adaptability",
};

const financial_performance = {
  "Revenue Growth": "Revenue Growth",
  "Profitability": "Profitability",
  "Return on Equity": "Return on Equity",
  "Stock Performance": "Stock Performance",
  "Debt Management": "Debt Management",
  "Free Cash Flow": "Free CashFlow",
};

const Pedigree = (props) => {
  const { report, handleReaction, isMiniListOpen } = props;
  const [ceo_info, setCeoInfo] = useState(null);
  const [age, setAge] = useState("");
  const isAIFeedbackEntitled = UserInfoUtil.IsUserEntitled(
    EntitlementType.AI_FEEDBACK_Entitlement
  );

  useEffect(() => {
    const new_report = parse_object(report);
    setCeoInfo(new_report);
    if (typeof new_report === "object" && new_report?.ceo_info?.dob) {
      const dob_age =
        new Date().getFullYear() -
        new Date(new_report.ceo_info.dob).getFullYear() -
        1;
      setAge(dob_age);
    }
  }, [report]);

  const parse_object = (report) => {
    try {
      let parsed_obj = JSON.parse(report);
      // in case it's stringified multiple times
      for (let i = 0; i <= 5; i++) {
        if (typeof parsed_obj === "object") break;
        parsed_obj = JSON.parse(parsed_obj);
      }
      return parsed_obj;
    } catch (error) {
      console.error("Invalid JSON string:", error);
      return report.plain_content;
    }
  };

  const List_from_object = ({ obj, name, replace_keys = true }) => {
    const [list, setList] = useState([]);

    useEffect(() => {
      let new_list = [];
      let new_obj = {};
      if (name === "financial_performance") {
        new_obj = financial_performance;
      } else if (name === "strategic_vision_and_execution") {
        new_obj = strategic_vision_and_execution;
      }
      if (replace_keys) {
        for (let key in obj) {
          new_list.push({
            title: new_obj[key] ?? key,
            description:
              typeof obj[key] === "object"
                ? obj[key]["description"]
                  ? obj[key]["description"]
                  : JSON.stringify(obj[key])
                : obj[key],
          });
        }
      } else {
        for (let key in obj) {
          new_list.push({
            title: key,
            description:
              typeof obj[key] === "object"
                ? obj[key]["description"]
                  ? obj[key]["description"]
                  : JSON.stringify(obj[key])
                : obj[key],
          });
        }
      }
      setList(new_list);
    }, [obj, name, replace_keys]);

    return (
      <ul>
        {list.map((val, idx) => (
          <li 
            key={idx}
            style={{
              position: "relative",
              listStyleType: isAIFeedbackEntitled ? "none" : "",
            }}
          >
            <p>
              <strong>{val.title}:</strong> {val.description}
            </p>
            <div style={{ position: "absolute", top: "-2px", left: "-28px" }}>
              {isAIFeedbackEntitled && <AIReactionModal
                  text={`${val.title}: ${val.description}`}
                  handleReaction={(text, reaction, comments) =>
                    handleReaction(
                      text,
                      reaction,
                      comments,
                    )
                  }
                  isInsights
                  isPedigree
                  isMiniListOpen={isMiniListOpen}
                />
              }
            </div>
          </li>
        ))}
      </ul>
    );
  };

  const overall_score =
    ceo_info && typeof ceo_info === "object"
      ? ceo_info["overall_score"] ?? ceo_info["Overall Score"] ?? null
      : null;

  return (
    <>
      {(ceo_info && typeof ceo_info === "object" && (
        <div
          className="header-block-1 box-shadow"
          style={{
            display: "grid",
            rowGap: 32, // was Box rowGap={4} → 4 * 8px
            padding: 20,
            margin: 20,
          }}
        >
          {/* Top Summary Row: Overall Score | Name & History | Meta + Image */}
          <Row gap={16}>
            {/* Overall Score */}
            <Col span={overall_score ? 3 : 2}>
            {overall_score && (
              <div className="header-block-1 box-shadow">
                  <div style={{ position: "relative" }}>
                    <p
                      className="pedigree-header-color-2"
                      style={{ textAlign: "center" }}
                    >
                      Overall Score
                    </p>
                    <h1
                      style={{ textAlign: "center" }}
                      className="pedigree-header-color-3"
                    >
                      {overall_score?.score}
                    </h1>
                    <p
                      style={{ textAlign: "center" }}
                      className="pedigree-header-color-2"
                    >
                      {overall_score?.description}
                    </p>
                    <div style={{ top: "0px", right: "10px", position: "absolute" }}>
                      { isAIFeedbackEntitled &&
                        <AIReactionModal
                          text={"Overall Score: " + overall_score?.score + " - " + overall_score?.description }
                          handleReaction={(text, reaction, comments) =>
                            handleReaction(
                              text,
                              reaction,
                              comments,
                            )
                          }
                          isInsights
                          isPedigree
                          isMiniListOpen={isMiniListOpen}
                        />
                      }
                    </div>
                  </div>
                </div>
              )}
            </Col>

            {/* Name + Position/Companies */}
            <Col span={6}>
              <Row gap={16}>
                <Col span={12} className="header-block-1 box-shadow">
                  {ceo_info?.ceo_info && (
                    <>
                      <p
                        style={{ textAlign: "center", fontSize: "large" }}
                        className="pedigree-header-1"
                      >
                        {ceo_info.ceo_info.name}
                      </p>
                      <p style={{ textAlign: "center" }}>
                        {age
                          ? `${age} years old`
                          : ceo_info.ceo_info?.age
                          ? `${ceo_info.ceo_info?.age} Years Old`
                          : ""}
                      </p>
                    </>
                  )}
                </Col>

                <Col
                  span={12}
                  className="box-shadow"
                  style={{
                    border: "3px solid grey",
                    borderRadius: "3px",
                    padding: "4px",
                  }}
                >
                  {ceo_info?.positions && (
                    <div>
                      <div style={{ display: "flex", gap: "4px", position: "relative" }}>
                        <p className="pedigree-header-2 pedigree-header-color-2" style={{ marginTop: "3px" }}>
                          POSITION HISTORY
                        </p>
                        {isAIFeedbackEntitled && <AIReactionModal
                            text={"POSITION HISTORY: " + JSON.stringify(ceo_info.positions)}
                            handleReaction={(text, reaction, comments) =>
                              handleReaction(
                                text,
                                reaction,
                                comments,
                              )
                            }
                            isInsights
                            isPedigree
                            isMiniListOpen={isMiniListOpen}
                          />
                        }
                      </div>
                      <ul>
                        {ceo_info.positions.map((val, ind) => {
                          const exp = val.experience;
                          const expText = exp
                            ? ` (${isNaN(Number(exp)) ? exp : `${exp} yrs`})`
                            : "";

                          const positionDetails = `${val.shortform || val.position}, ${val.company}, ${val.duration}${expText}`;
                          return (
                            <li
                              key={`${val.company}-${val.position}-${ind}`}
                              style={{
                                fontWeight: ind === 0 ? "bold" : "normal",
                                position: "relative",
                                listStyleType: isAIFeedbackEntitled ? "none" : "",
                              }}
                            >
                              {positionDetails}
                              <div style={{ position: "absolute", top: "-4px", left: "-28px" }}>
                                {isAIFeedbackEntitled &&
                                  <AIReactionModal
                                    text={"POSITION HISTORY: " + positionDetails}
                                    handleReaction={(text, reaction, comments) =>
                                      handleReaction(
                                        text,
                                        reaction,
                                        comments,
                                      )
                                    }
                                    isInsights
                                    isPedigree
                                    isMiniListOpen={isMiniListOpen}
                                  />
                                }
                              </div>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  )}

                  {ceo_info?.companies && (
                    <div style={{ marginTop: "6px" }}>
                      <div style={{ display: "flex", gap: "4px", position: "relative" }}>
                        <p className="pedigree-header-2 pedigree-header-color-2" style={{ marginTop: "3px" }}>
                          PREVIOUS COMPANIES
                        </p>
                        {isAIFeedbackEntitled && <AIReactionModal
                            text={"PREVIOUS COMPANIES: " + JSON.stringify(ceo_info.companies)}
                            handleReaction={(text, reaction, comments) =>
                              handleReaction(
                                text,
                                reaction,
                                comments,
                              )
                            }
                            isInsights
                            isPedigree
                            isMiniListOpen={isMiniListOpen}
                          />
                        }
                      </div>
                      <ul>
                        {ceo_info.companies.map((val, idx) => (
                          <li 
                            key={`${val.company}-${val.duration}-${idx}`} 
                            style={{
                              position: "relative",
                              listStyleType: isAIFeedbackEntitled ? "none" : "",
                            }}
                          >
                            {val.company}, {val.duration}
                            <div style={{ position: "absolute", top: "-4px", left: "-28px" }}>
                              {isAIFeedbackEntitled &&
                                <AIReactionModal
                                  text={"PREVIOUS COMPANIES: " + val.company + ", " + val.duration}
                                  handleReaction={(text, reaction, comments) =>
                                    handleReaction(
                                      text,
                                      reaction,
                                      comments,
                                    )
                                  }
                                  isInsights
                                  isPedigree
                                  isMiniListOpen={isMiniListOpen}
                                />
                              }
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Col>
              </Row>
            </Col>

            {/* Report date + Image */}
            <Col span={3} style={{ position: "relative" }}>
              <div className="ceo-pedigree-image-container box-shadow">
                {(ceo_info?.ceo_info?.image && (
                  <img
                    id="decodedImage"
                    src={`data:image/png;base64,${ceo_info.ceo_info.image}`}
                    alt="CEO"
                    className="ceo-pedigree-image"
                  />
                )) || (
                  <div className="ceo-pedigree-placeholder">
                    <p style={{ textAlign: "center" }}>
                      No Image Available
                    </p>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Salary/Shares | Directorships */}
          <Row gap={16}>
            <Col span={5}>
              {(ceo_info?.salary_info || ceo_info?.shares) && (
                <div className="header-block-1 box-shadow">
                  {ceo_info?.salary_info && (
                    <div>
                      <div style={{ display: 'flex' }}>
                        <p className="pedigree-header-2 pedigree-header-color-2" style={{ marginTop: "4px" }}>SALARY INFO</p>
                        <div style={{ marginLeft: "4px" }}>
                          {isAIFeedbackEntitled && <AIReactionModal
                              text={"SALARY INFO" + '-' + ceo_info.salary_info}
                              handleReaction={(text, reaction, comments) =>
                                handleReaction(
                                  text,
                                  reaction,
                                  comments,
                                )
                              }
                              isInsights
                              isPedigree
                              isMiniListOpen={isMiniListOpen}
                            />
                          }
                        </div>
                      </div>
                      <ul>
                        {ceo_info.salary_info.map((val, idx) => (
                          <li 
                            key={`${val.year}-${idx}`}
                            style={{
                              position: "relative",
                              listStyleType: isAIFeedbackEntitled ? "none" : "",
                            }}
                          >
                            {val.year} - ${val.compensation}
                            <div style={{ position: "absolute", top: "-3px", left: "-28px" }}>
                              {isAIFeedbackEntitled && <AIReactionModal
                                  text={'Salary Info' + '-' + val.year + ':' + val.compensation}
                                  handleReaction={(text, reaction, comments) =>
                                    handleReaction(
                                      text,
                                      reaction,
                                      comments,
                                    )
                                  }
                                  isInsights
                                  isPedigree
                                  isMiniListOpen={isMiniListOpen}
                                />
                              }
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {ceo_info?.shares && (
                    <div style={{ marginTop: "10px" }}>
                      <div style={{ display: 'flex' }}>
                        <p className="pedigree-header-color-2" style={{ marginTop: "4px" }}>SHARES</p>
                        <div style={{ marginLeft: "4px" }}>
                          {isAIFeedbackEntitled && <AIReactionModal
                              text={"SHARES" + '-' + ceo_info.shares}
                              handleReaction={(text, reaction, comments) =>
                                handleReaction(
                                  text,
                                  reaction,
                                  comments,
                                )
                              }
                              isInsights
                              isPedigree
                              isMiniListOpen={isMiniListOpen}
                            />
                          }
                        </div>
                      </div>
                      <ul>
                        <li
                          style={{
                            position: "relative",
                            listStyleType: isAIFeedbackEntitled ? "none" : "",
                          }}
                        >
                          <p>
                            Current Market Value: {ceo_info.shares.value}
                          </p>
                          <div style={{ position: "absolute", top: "-3px", left: "-28px" }}>
                            {isAIFeedbackEntitled && <AIReactionModal
                                text={'Current Market Value' + ':' + ceo_info.shares.value}
                                handleReaction={(text, reaction, comments) =>
                                  handleReaction(
                                    text,
                                    reaction,
                                    comments,
                                  )
                                }
                                isInsights
                                isPedigree
                                isMiniListOpen={isMiniListOpen}
                              />
                            }
                          </div>
                        </li>
                        <li
                          style={{
                            position: "relative",
                            listStyleType: isAIFeedbackEntitled ? "none" : "",
                          }}
                        >
                          <p>Shares Count: {ceo_info.shares.total_hr}</p>
                          <div style={{ position: "absolute", top: "-3px", left: "-28px" }}>
                            {isAIFeedbackEntitled && <AIReactionModal
                                text={'Shares Count' + ':' + ceo_info.shares.total_hr}
                                handleReaction={(text, reaction, comments) =>
                                  handleReaction(
                                    text,
                                    reaction,
                                    comments,
                                  )
                                }
                                isInsights
                                isPedigree
                                isMiniListOpen={isMiniListOpen}
                              />
                            }
                          </div>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </Col>

            <Col span={7}>
              {ceo_info?.directorships && (
                <div className="header-block-1 box-shadow">
                  <div style={{ display: 'flex' }}>
                    <p className="pedigree-header-2 pedigree-header-color-2" style={{ marginTop: "4px" }}>DIRECTORSHIPS</p>
                    <div style={{ marginLeft: "4px" }}>
                      {isAIFeedbackEntitled && <AIReactionModal
                          text={"DIRECTORSHIPS" + '-' + ceo_info.directorships}
                          handleReaction={(text, reaction, comments) =>
                            handleReaction(
                              text,
                              reaction,
                              comments,
                            )
                          }
                          isInsights
                          isPedigree
                          isMiniListOpen={isMiniListOpen}
                        />
                      }
                    </div>
                  </div>
                  <ul>
                    {ceo_info.directorships.map((val, idx) => (
                      <li 
                        key={idx}
                        style={{
                          position: "relative",
                          listStyleType: isAIFeedbackEntitled ? "none" : "",
                        }}
                      >
                        {val}
                        <div style={{ position: "absolute", top: "-3px", left: "-28px" }}>
                          {isAIFeedbackEntitled && <AIReactionModal
                              text={'DIRECTORSHIPS' + '-' + val}
                              handleReaction={(text, reaction, comments) =>
                                handleReaction(
                                  text,
                                  reaction,
                                  comments,
                                )
                              }
                              isInsights
                              isPedigree
                              isMiniListOpen={isMiniListOpen}
                            />
                          }
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Col>
          </Row>

          {/* Summary */}
          <div className="header-block-1 box-shadow">
            <div style={{ display: "flex", position: "relative", gap: "4px" }}>
              <h4 className="pedigree-header-1 text-uppercase" style={{ marginTop: "5px" }}>
                {ceo_info?.ceo_info?.name} Summary
              </h4>
              {isAIFeedbackEntitled && <AIReactionModal
                  text={`${ceo_info?.ceo_info?.name} Summary` + ", " + (ceo_info.summary_narrative ? JSON.stringify(ceo_info.summary_narrative) : JSON.stringify(ceo_info.summary))}
                  handleReaction={(text, reaction, comments) =>
                    handleReaction(
                      text,
                      reaction,
                      comments,
                    )
                  }
                  isInsights
                  isPedigree
                  isMiniListOpen={isMiniListOpen}
                />
              }
            </div>
            <ul>
              {(ceo_info?.summary_narrative &&
                ceo_info.summary_narrative.map((val, idx) => (
                  <li 
                    key={`sn-${idx}`}
                    style={{
                      position: "relative",
                      listStyleType: isAIFeedbackEntitled ? "none" : "",
                    }}
                  >
                    <div style={{ display: "flex", position: "relative" }}>
                      <p>
                        <strong>{val.title}:</strong> {val.description}
                      </p>
                      <div style={{ position: "absolute", top: "-2px", left: "-28px" }}>
                        {isAIFeedbackEntitled && <AIReactionModal
                            text={`${ceo_info?.ceo_info?.name} Summary` + '-' + val.title + ':' + val.description}
                            handleReaction={(text, reaction, comments) =>
                              handleReaction(
                                text,
                                reaction,
                                comments,
                              )
                            }
                            isInsights
                            isPedigree
                            isMiniListOpen={isMiniListOpen}
                          />
                        }
                      </div>
                    </div>
                  </li>
                ))) ||
                (ceo_info?.summary &&
                  ceo_info.summary.map((val, idx) => (
                    <li 
                      key={`s-${idx}`}
                      style={{
                        position: "relative",
                        listStyleType: isAIFeedbackEntitled ? "none" : "",
                      }}
                    >
                      <div style={{ display: "flex", position: "relative" }}>
                        <p>
                          <strong>{val.title}:</strong> {val.description}
                        </p>
                        <div style={{ position: "absolute", top: "-2px", left: "-28px" }}>
                          {isAIFeedbackEntitled && <AIReactionModal
                              text={`${ceo_info?.ceo_info?.name} Summary` + '-' + val.title + ':' + val.description}
                              handleReaction={(text, reaction, comments) =>
                                handleReaction(
                                  text,
                                  reaction,
                                  comments,
                                )
                              }
                              isInsights
                              isPedigree
                              isMiniListOpen={isMiniListOpen}
                            />
                          }
                        </div>
                      </div>
                    </li>
                  )))}
            </ul>
          </div>

          {/* Financial Performance */}
          <Row gap={16}>
            <Col span={4}>
              <div className="header-block-1 box-shadow" style={{ position: "relative" }}>
                <p
                  style={{ textAlign: "center" }}
                  className="pedigree-header-color-2"
                >
                  Score
                </p>
                {ceo_info?.financial_performance && (
                  <>
                    <h1
                      style={{ textAlign: "center" }}
                      className="pedigree-header-color-3"
                    >
                      {ceo_info.financial_performance_score?.score}
                    </h1>
                    <p
                      style={{ textAlign: "center" }}
                      className="pedigree-header-color-2"
                    >
                      {ceo_info.financial_performance_score?.description}
                    </p>
                  </>
                )}
                <div style={{ top: "0px", right: "10px", position: "absolute" }}>
                  { isAIFeedbackEntitled &&
                    <AIReactionModal
                      text={"Score: " + ceo_info.financial_performance_score?.score + " - " + ceo_info.financial_performance_score?.description }
                      handleReaction={(text, reaction, comments) =>
                        handleReaction(
                          text,
                          reaction,
                          comments,
                        )
                      }
                      isInsights
                      isPedigree
                      isMiniListOpen={isMiniListOpen}
                    />
                  }
                </div>
              </div>
            </Col>

            <Col span={8}>
              <div className="header-block-1 box-shadow">
                <div style={{ display: "flex", position: "relative", gap: "4px" }}>
                  <h4 className="pedigree-header-1" style={{ marginTop: "5px" }}>
                    FINANCIAL PERFORMANCE                    
                  </h4>
                  {isAIFeedbackEntitled && <AIReactionModal
                      text={`FINANCIAL PERFORMANCE` + JSON.stringify(ceo_info.financial_performance)}
                      handleReaction={(text, reaction, comments) =>
                        handleReaction(
                          text,
                          reaction,
                          comments,
                        )
                      }
                      isInsights
                      isPedigree
                      isMiniListOpen={isMiniListOpen}
                    />
                  }
                </div>
                {ceo_info?.financial_performance && (
                  <List_from_object
                    obj={ceo_info.financial_performance}
                    name={"financial_performance"}
                    replace_keys={false}
                  />
                )}
              </div>
            </Col>
          </Row>

          {/* Strategic Vision & Execution */}
          <Row gap={16}>
            <Col span={4}>
              <div className="header-block-1 box-shadow" style={{ position: "relative" }}>
                <p
                  style={{ textAlign: "center" }}
                  className="pedigree-header-color-2"
                >
                  Score
                </p>
                {ceo_info?.strategic_execution_score && (
                  <>
                    <h1
                      style={{ textAlign: "center" }}
                      className="pedigree-header-color-3"
                    >
                      {ceo_info.strategic_execution_score?.score}
                    </h1>
                    <p
                      style={{ textAlign: "center" }}
                      className="pedigree-header-color-2"
                    >
                      {ceo_info.strategic_execution_score?.description}
                    </p>
                  </>
                )}
                <div style={{ top: "0px", right: "10px", position: "absolute" }}>
                  { isAIFeedbackEntitled &&
                    <AIReactionModal
                      text={"Score: " + ceo_info.strategic_execution_score?.score + " - " + ceo_info.strategic_execution_score?.description }
                      handleReaction={(text, reaction, comments) =>
                        handleReaction(
                          text,
                          reaction,
                          comments,
                        )
                      }
                      isInsights
                      isPedigree
                      isMiniListOpen={isMiniListOpen}
                    />
                  }
                </div>
              </div>
            </Col>

            <Col span={8}>
              <div className="header-block-1 box-shadow">
                <div style={{ display: "flex", position: "relative", gap: "4px" }}>
                  <h4 className="pedigree-header-1" style={{ marginTop: "5px" }}>
                    STRATEGIC VISION AND EXECUTION
                  </h4>
                  {isAIFeedbackEntitled && <AIReactionModal
                      text={`STRATEGIC VISION AND EXECUTION` + JSON.stringify(ceo_info.strategic_execution)}
                      handleReaction={(text, reaction, comments) =>
                        handleReaction(
                          text,
                          reaction,
                          comments,
                        )
                      }
                      isInsights
                      isPedigree
                      isMiniListOpen={isMiniListOpen}
                    />
                  }
                </div>
                {ceo_info?.strategic_execution && (
                  <List_from_object
                    obj={ceo_info.strategic_execution}
                    name={"strategic_vision_and_execution"}
                    replace_keys={false}
                  />
                )}
              </div>
            </Col>
          </Row>

          {/* Educational History */}
          <Row gap={16}>
            <Col span={4}>
              <div className="header-block-1 box-shadow" style={{ position: "relative" }}>
                <p
                  style={{ textAlign: "center" }}
                  className="pedigree-header-color-2"
                >
                  Score
                </p>
                {ceo_info?.education_history_score && (
                  <>
                    <h1
                      style={{ textAlign: "center" }}
                      className="pedigree-header-color-3"
                    >
                      {ceo_info.education_history_score?.score}
                    </h1>
                    <p
                      style={{ textAlign: "center" }}
                      className="pedigree-header-color-2"
                    >
                      {ceo_info.education_history_score?.description}
                    </p>
                  </>
                )}
                <div style={{ top: "0px", right: "10px", position: "absolute" }}>
                  { isAIFeedbackEntitled &&
                    <AIReactionModal
                      text={"Score: " + ceo_info.education_history_score?.score + " - " + ceo_info.education_history_score?.description }
                      handleReaction={(text, reaction, comments) =>
                        handleReaction(
                          text,
                          reaction,
                          comments,
                        )
                      }
                      isInsights
                      isPedigree
                      isMiniListOpen={isMiniListOpen}
                    />
                  }
                </div>
              </div>
            </Col>

            <Col span={8}>
              <div className="header-block-1 box-shadow">
                <div style={{ display: "flex", position: "relative", gap: "4px" }}>
                  <h4 className="pedigree-header-1" style={{ marginTop: "5px" }}>
                    EDUCATIONAL HISTORY
                  </h4>
                  {isAIFeedbackEntitled && <AIReactionModal
                      text={`EDUCATIONAL HISTORY` + JSON.stringify(ceo_info.education_history)}
                      handleReaction={(text, reaction, comments) =>
                        handleReaction(
                          text,
                          reaction,
                          comments,
                        )
                      }
                      isInsights
                      isPedigree
                      isMiniListOpen={isMiniListOpen}
                    />
                  }
                </div>
                {ceo_info?.education_history && (
                  <div>
                    <ul>
                      {ceo_info.education_history.map((val, idx) => (
                        <React.Fragment key={`edu-${idx}`}>
                          <li style={{ position: "relative", listStyleType: isAIFeedbackEntitled ? "none" : "" }}>
                            <strong>
                              {val.degree}
                              {val.shortform ? ` (${val.shortform})` : ""}
                            </strong>
                            <div style={{ position: "absolute", top: "-2px", left: "-28px" }}>
                              {isAIFeedbackEntitled && <AIReactionModal
                                  text={`EDUCATIONAL HISTORY` + JSON.stringify(val)}
                                  handleReaction={(text, reaction, comments) =>
                                    handleReaction(
                                      text,
                                      reaction,
                                      comments,
                                    )
                                  }
                                  isInsights
                                  isPedigree
                                  isMiniListOpen={isMiniListOpen}
                                />
                              }
                            </div>
                          </li>
                          <ul>
                            <li>
                              <p>
                                <strong>Institution:</strong> {val.institution}
                              </p>
                            </li>
                            <li>
                              <p>
                                <strong>Years Attended:</strong> {val.year}
                              </p>
                            </li>
                            {val.achievement && (
                              <li>
                                <p>
                                  <strong>Notable Achievement:</strong>{" "}
                                  {val.achievement}
                                </p>
                              </li>
                            )}
                          </ul>
                        </React.Fragment>
                      ))}
                    </ul>
                  </div>
                )}
                {ceo_info?.education_history && (
                  <div>
                    <h4>
                      <div style={{ display: "flex", position: "relative" }}>
                        Relevance and Institution Quality
                        <div style={{ position: "absolute", top: "-4px", left: "245px" }}>
                          {isAIFeedbackEntitled && <AIReactionModal
                              text={`Relevance and Institution Quality` + JSON.stringify(ceo_info.education_history)}
                              handleReaction={(text, reaction, comments) =>
                                handleReaction(
                                  text,
                                  reaction,
                                  comments,
                                )
                              }
                              isInsights
                              isPedigree
                              isMiniListOpen={isMiniListOpen}
                            />
                          }
                        </div>
                      </div>
                    </h4>
                    <ul>
                      <li style={{ position: "relative", listStyleType: isAIFeedbackEntitled ? "none" : "" }}>
                        <strong>Relevance</strong>
                        <div style={{ position: "absolute", top: "-2px", left: "-28px" }}>
                          {isAIFeedbackEntitled && <AIReactionModal
                              text={`EDUCATIONAL HISTORY Relevance` + JSON.stringify(ceo_info.education_history)}
                              handleReaction={(text, reaction, comments) =>
                                handleReaction(
                                  text,
                                  reaction,
                                  comments,
                                )
                              }
                              isInsights
                              isPedigree
                              isMiniListOpen={isMiniListOpen}
                            />
                          }
                        </div>
                      </li>
                      <ul>
                        {ceo_info.education_history.map(
                          (val, idx) =>
                            val.relevance && (
                              <li key={`rel-${idx}`}>
                                <strong>{val.shortform}:</strong>{" "}
                                {val.relevance}
                              </li>
                            )
                        )}
                      </ul>
                      <li style={{ position: "relative", listStyleType: isAIFeedbackEntitled ? "none" : "" }}>
                        <strong>Institution Quality</strong>
                        <div style={{ position: "absolute", top: "-2px", left: "-28px" }}>
                          {isAIFeedbackEntitled && <AIReactionModal
                              text={`EDUCATIONAL HISTORY Institution Quality` + JSON.stringify(ceo_info.education_history)}
                              handleReaction={(text, reaction, comments) =>
                                handleReaction(
                                  text,
                                  reaction,
                                  comments,
                                )
                              }
                              isInsights
                              isPedigree
                              isMiniListOpen={isMiniListOpen}
                            />
                          }
                        </div>
                      </li>
                      <ul>
                        {ceo_info.education_history.map(
                          (val, idx) =>
                            val.prestige &&
                            val.institution && (
                              <li key={`prest-${idx}`}>
                                <strong>{val.institution}:</strong>{" "}
                                {val.prestige}
                              </li>
                            )
                        )}
                      </ul>
                    </ul>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Peer Comparison */}
          <div
            className="ceo-list-type box-shadow"
            style={{
              border: "3px solid grey",
              borderRadius: "3px",
              padding: "4px",
            }}
          >
            <div style={{ display: "flex", position: "relative", gap: "4px" }}>
              <h4 className="pedigree-header-1" style={{ marginTop: "5px" }}>
                INDUSTRY PEER COMPARISON
              </h4>
              {isAIFeedbackEntitled && <AIReactionModal
                  text={`INDUSTRY PEER COMPARISON` + JSON.stringify(ceo_info.comparison)}
                  handleReaction={(text, reaction, comments) =>
                    handleReaction(
                      text,
                      reaction,
                      comments,
                    )
                  }
                  isInsights
                  isPedigree
                  isMiniListOpen={isMiniListOpen}
                />
              }
            </div>
            { isAIFeedbackEntitled ? 
              <ul>
                {ceo_info?.comparison &&
                  ceo_info.comparison.map((val, index) => (
                    <li
                      key={`${val.name}-${val.company ?? "n/a"}-${index}`}
                      style={{ color: index === 0 ? "orange" : "", listStyleType: "none", position: "relative" }}
                    >
                      <p>
                        <strong>
                          {val.name} {val.company ? `(${val.company})` : ""}
                        </strong>{" "}
                      </p>
                      <p>{val.contribution}</p>
                      <div style={{ top: "-2px", left: "-28px", position: "absolute" }}>
                        <AIReactionModal
                          text={"INDUSTRY PEER COMPARISON: " + `${val.name} ${val.company ? `(${val.company})` : ""}`}
                          handleReaction={(text, reaction, comments) =>
                            handleReaction(
                              text,
                              reaction,
                              comments,
                            )
                          }
                          isInsights
                          isPedigree
                          isMiniListOpen={isMiniListOpen}
                        />
                      </div>
                    </li>
                  ))}
              </ul>
            :
              <ol>
                {ceo_info?.comparison &&
                  ceo_info.comparison.map((val, index) => (
                    <li
                      key={`${val.name}-${val.company ?? "n/a"}-${index}`}
                      style={{ color: index === 0 ? "orange" : "" }}
                    >
                      <p>
                        <strong>
                          {val.name} {val.company ? `(${val.company})` : ""}
                        </strong>{" "}
                      </p>
                      <p>{val.contribution}</p>
                    </li>
                  ))}
              </ol>
            }
          </div>
        </div>
      )) ||
        (report.plain_content === "No Data Available" ||
        report.plain_content === "Something Went Wrong!" ? (
          <p style={{ textAlign: "center", marginTop: "20px" }}>
            {report.plain_content}
          </p>
        ) : (
          <p>{report.plain_content}</p>
        ))}
    </>
  );
};

export default Pedigree;
